<template>
  <div>
    <Head title="Edit Customer" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-orange-400 hover:text-orange-600" href="/customers">Customers</Link>
      <span class="text-orange-400 font-medium">/</span> Edit
    </h1>
    <div class="bg-white rounded-md shadow">
      <form @submit.prevent="update">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <text-input v-show="customer.status == 1" v-model="form.number" :error="form.errors.number" aria-readonly="readonly" class="pb-8 pr-6 w-full lg:w-full" label="Number" />
          <text-input v-model="form.name" :error="form.errors.name" class="pb-8 pr-6 w-full lg:w-full" label="Name" />
          <text-input v-model="form.address" :error="form.errors.address" class="pb-8 pr-6 w-full lg:w-full" label="Address" />
          <div class="w-full lg:w-full pb-8 pr-6">
            <label class="form-label">City:</label>
            <multiselect
              id="city"
              v-model="selectedCity"
              placeholder="Search cities"
              :options="cities"
              :close-on-select="true"
              :searchable="true"
              :error="form.errors.city"
              :style="form.errors.city ? 'border: var(--ms-border-width,1px) solid var(--ms-border-color,red)' : ''"
              @search-change="onSearchCityChange"
              @input="onSelectedCity"
            />
            <div v-if="form.errors.city" class="form-error">{{ form.errors.city }}</div>
          </div>
          <text-input v-model="form.email" :error="form.errors.email" class="pb-8 pr-6 w-1/2 lg:w-1/2" label="Email" />
          <text-input v-model="form.phone" :error="form.errors.phone" class="pb-8 pr-6 w-1/2 lg:w-1/2" label="Phone" />
          <text-input v-model="form.kra_pin_number" :error="form.errors.kra_pin_number" class="pb-8 pr-6 w-full lg:w-full" label="KRA Pin Number"/>
          <select-input v-model="form.channel" :error="form.errors.channel" class="pb-8 pr-6 w-full lg:w-full" label="Channel">
            <option :value="null">
              Select Channel
            </option>
            <option
              v-for="channel in channels"
              :key="channel.id"
              :value="channel.id"
            >
              {{ channel.name }}
            </option>
          </select-input>
          <text-input v-model="form.country_code" :error="form.errors.country_code" class="pb-8 pr-6 w-1/2 lg:w-1/2" label="Country Code" />
          <select-input v-model="form.customer_branch_id" :error="form.errors.customer_branch_id" class="pb-8 pr-6 w-full lg:w-full" label="Customer Branch">
            <option :value="null">
              Select Customer Branch
            </option>
            <option
              v-for="customer_branch in customerBranches"
              :key="customer_branch.value"
              :value="customer_branch.value"
            >
              {{ customer_branch.label }}
            </option>
          </select-input>
          <div class="pb-2 pr-6 mb-3 w-full lg:w-full">
            <Checkbox label="KYC" :active="kyc" @input="kyc = $event"/>
          </div>
          <div class="pb-8 pr-6 w-full lg:w-full">
            <label class="mt-5">Attach Customer Documents:</label>
            <hr class="mt-3 mb-5">
            <div
              v-for="(document, dindex) in form.documents"
              :key="dindex"
              class="flex flex-wrap w-full"
            >
              <select-input
                :key="dindex"
                v-model="document.document_id"
                :error="showValidationErrors(`documents.${dindex}.document_id`)"
                class="pb-8 pr-6 w-full lg:w-4/12"
              >
                <option :value="null">
                  Select Document Type
                </option>
                <option
                  v-for="documentObj in documents"
                  :key="documentObj.value"
                  :value="documentObj.value"
                >
                  {{ documentObj.label }}
                </option>
              </select-input>
              <div :key="dindex" class="pb-8 pr-6 w-full lg:w-4/12">
                <file-input
                  :key="dindex"
                  v-model="document.file"
                  :error="showValidationErrors(`documents.${dindex}.file`)"
                  type="file" accept="image/*"/>
                <span
                  v-if="isValidationErrors(`documents.${dindex}.file`)"
                  class="mt-3"
                >
                  <span class="form-error">
                    {{ showValidationErrors(`documents.${dindex}.file`) }}
                  </span>
                </span>
              </div>
              <div :key="dindex" class="pb-8 pr-6 w-full lg:w-2/12">
                <img v-show="document.image" :src="`/storage/${document.image}`" width="200" height="150" />
              </div>
              <div class="pb-2 pr-6 w-full lg:w-2/12">
                <div class="w-1/2 lg:w-1/2">
                  <svg
                    v-show="form.documents.length > 1"
                    @click="removeDocument(dindex, form.documents)"
                    xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" style="color: red;">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div class="w-1/2 lg:w-1/2">
                  <svg
                    v-show="(dindex === countDocuments && (form.documents.length <= (documents.length - 1)))"
                    @click="addDocument"
                    xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <div class="pb-8 pr-6 w-full lg:w-full">
            <label class="mt-5">Referees:</label>
            <hr class="mt-3 mb-5">
            <div
              v-for="(referee, findex) in form.referees"
              :key="findex"
              class="flex flex-wrap w-full"
            >
            <text-input v-model="referee.name" :error="showValidationErrors(`referees.${findex}.name`)" class="pb-8 pr-6 w-1/2 lg:w-1/2" label="Name" />
            <text-input v-model="referee.phone_number" :error="showValidationErrors(`referees.${findex}.phone_number`)" class="pb-8 pr-6 w-1/2 lg:w-1/2" label="Phone Number" />
            </div>
          </div>
          <div class="w-full lg:w-full pb-8 pr-6">
            <label class="form-label">Payment Terms:</label>
            <multiselect
              id="payment_term"
              v-model="form.payment_term_id"
              placeholder="Search Payment Term"
              :options="paymentTerms"
              :close-on-select="true"
              :searchable="true"
              :error="form.errors.payment_term_id"
              :style="form.errors.payment_term_id ? 'border: var(--ms-border-width,1px) solid var(--ms-border-color,red)' : ''"
            />
            <div v-if="form.errors.payment_term_id" class="form-error">{{ form.errors.payment_term_id }}</div>
          </div>
        </div>
        <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button :loading="form.processing" class="btn-orange" type="submit">Update Customer</loading-button>
        </div>
      </form>
    </div>
  </div>
</template>
    
<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import Multiselect from '@vueform/multiselect'
import throttle from 'lodash/throttle'
import FileInput from '@/Shared/FileInput'
import LoadingButton from '@/Shared/LoadingButton'
import Checkbox from '@/Shared/Checkbox'
    
export default {
  components: {
    Head,
    Link,
    LoadingButton,
    TextInput,
    SelectInput,
    Multiselect,
    FileInput,
    Checkbox,
  },
  layout: Layout,
  remember: 'form',
  props: {
    customer: Object,
    channels: Object,
    cities: Object,
    documents: Object,
    customer_documents: Object,
    referees: Object,
    paymentTerms: Object,
    customerBranches: Object,
  },
  data() {
    return {
      kyc: this.customer.kyc ? true : false,
      form: this.$inertia.form({
        _method: 'put',
        name: this.customer.name,
        number: this.customer.number,
        city: this.customer.city_id,
        channel: this.customer.channel_id,
        customer_branch_id: this.customer.customer_branch_id,
        country_code: this.customer.country_code,
        phone: this.customer.phone,
        alternative_phone: this.customer.alternative_phone,
        address: this.customer.address,
        email: this.customer.email,
        kra_pin_number: this.customer.kra_pin_number,
        documents: this.customer_documents,
        referees: this.referees,
        payment_term_id: this.customer.payment_term_id,
      }),
      selectedCity: this.customer.city_id,
    }
  },
  computed: {
    countDocuments() {
      if (this.form.documents.length === 0) {
        return 0;
      }
      return this.form.documents.length - 1;
    },
  },
  methods: {
    addDocument(e) {
      e.preventDefault();
      this.form.documents.push({
        document_id: null,
        file: null,
        id: null,
      });
    },
    removeDocument(index, documents) {
      documents.splice(index, 1)
    },
    isValidationErrors(value) {
      if (this.form.errors) {
        const error = this.form.errors[value] || '';
        return !!error;
      }
      return false;
    },
    showValidationErrors(value) {
      if (this.form.errors) {
        const error = this.form.errors[value] || '';
        return error || '';
      }
      return '';
    },
    update() {
      this.form.transform(({...data }) => ({
        kyc: this.kyc,
        ...data,
      }));
      this.form.post(`/customers/${this.customer.id}`, this.form)
    },onSelectedCity(city, id) {
      this.form.city = city
      this.selectedCity = city
    },
    onSearchCityChange: throttle(function (city) {
    
    }),
  },
}
</script>
<style src="@vueform/multiselect/themes/default.css"></style>
<style type="text/css">
  .multiselect-option.is-selected.is-pointed {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
  .multiselect-option.is-selected {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
</style>
  